import {createContext, useCallback, useContext, useState} from 'react'

import {clearTracesFromSession, getTracesFromSession, setTracesToSession} from './sessionStorage'

import type {PluginProvider} from '../type'
import type {Trace} from './type'

const MAX_TRACE_COUNT = 500

interface TraceContextValue {
    traces: Trace[]
    addTrace: (trace: Trace) => void
    clearTraces: () => void
}

const TraceContext = createContext<TraceContextValue | undefined>(undefined)

export const useTraceContext = () => {
    const context = useContext(TraceContext)
    if (!context) {
        throw new Error('useTraceContext는 TraceProvider 내부에서만 사용할 수 있습니다.')
    }
    return context
}

const TraceProvider: PluginProvider = ({children}) => {
    const [traces, setTraces] = useState<TraceContextValue['traces']>(() => getTracesFromSession())

    const addTrace: TraceContextValue['addTrace'] = useCallback((trace) => {
        setTraces((prev) => {
            const next = [...prev, trace]
            const sliced = next.length > MAX_TRACE_COUNT ? next.slice(-MAX_TRACE_COUNT) : next
            setTracesToSession(sliced)
            return sliced
        })
    }, [])

    const clearTraces: TraceContextValue['clearTraces'] = useCallback(() => {
        clearTracesFromSession()
        setTraces([])
    }, [])

    return <TraceContext.Provider value={{traces, addTrace, clearTraces}}>{children}</TraceContext.Provider>
}

export default TraceProvider
